import { ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BOOKING_URL } from "@/lib/site";
import { MaskReveal, Reveal } from "../Reveal";
import { PHOTO_SIZES, PHOTOS } from "../images";

const FEATURES = [
  "Pull-Through Sites",
  "Back-In Sites",
  "Full Hookups",
  "30-Amp Service",
  "50-Amp Service",
  "Water Connections",
  "Sewer Connections",
  "Electric Service",
  "Cable Television",
  "Wi-Fi",
  "Patio Sites Available",
  "Covered RV Options",
  "Short and Extended Stays",
] as const;

export function RvSitesSection() {
  const photo = PHOTOS.rvSite;

  return (
    <section
      id="rv-sites"
      className="scroll-mt-28 bg-surface py-24 sm:py-28 lg:py-32"
      aria-labelledby="rv-sites-title"
    >
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10">
        <div className="grid items-center gap-14 lg:grid-cols-[1.1fr_1fr] lg:gap-20">
          <div>
            <Reveal>
              <p className="eyebrow text-clay">Full-Service RV Camping</p>
              <h2
                id="rv-sites-title"
                className="mt-4 font-display text-3xl leading-tight sm:text-4xl lg:text-[2.9rem]"
              >
                Spacious Sites for RVs of Different Sizes
              </h2>
            </Reveal>

            <Reveal
              delay={0.08}
              className="mt-6 space-y-5 text-base leading-relaxed text-muted-foreground sm:text-lg"
            >
              <p>
                Amigo Inn &amp; RV Park features spacious pull-through and back-in RV sites designed
                to accommodate a variety of RV sizes. The park offers both 30- and 50-amp service,
                and full-hookup sites include water, sewer, and electric connections.
              </p>
              <p>
                Guests can also ask about patio sites and covered RV spaces that provide additional
                comfort and shade during the warmer South Texas months.
              </p>
              <p>
                Whether you are staying for a weekend, visiting for work, escaping the winter, or
                spending an entire season in Zapata, our team will help you find an option that fits
                your stay.
              </p>
            </Reveal>

            <Reveal delay={0.12} className="mt-10">
              <Button asChild variant="book" size="cta">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                  Find an RV Site <ArrowRight aria-hidden="true" />
                </a>
              </Button>
            </Reveal>
          </div>

          <MaskReveal className="media-zoom rounded-sm">
            <img
              src={photo.src}
              srcSet={photo.srcSet}
              sizes={PHOTO_SIZES.half}
              alt={photo.alt}
              width={photo.width}
              height={photo.height}
              loading="lazy"
              decoding="async"
              className="aspect-[5/4] w-full rounded-sm object-cover"
            />
          </MaskReveal>
        </div>

        <ul className="mt-16 grid gap-x-8 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((feature, i) => (
            <Reveal as="li" key={feature} delay={(i % 3) * 0.04}>
              <div className="flex items-center gap-3 border-b border-sand/70 py-4">
                <Check className="size-4 shrink-0 text-forest" aria-hidden="true" />
                <span className="text-sm font-medium text-forest-deep">{feature}</span>
              </div>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
}
